/**
 * Permissões de menu no cliente. A fonte de verdade é o backend; isto é defesa
 * em profundidade (rotas) e decisão de exibir links (cards/botões).
 */

export const isFullAdmin = user => {
  if (!user) return false;
  if (user.super === true) return true;
  const profile = String(user.profile || "").toLowerCase();
  return profile === "admin" || profile === "super" || profile === "superadmin";
};

/** Quem abre /profile-permissions e chama GET/PUT /api/menu-permissions. */
export const canManageMenuMatrix = user => {
  if (!user) return false;
  if (user.super === true) return true;
  const profile = String(user.profile || "").toLowerCase();
  return profile === "admin" || profile === "super";
};

export const hasMenuPermission = (user, permissionKey) => {
  if (!permissionKey) return true;
  if (isFullAdmin(user)) return true;
  const permissions = user?.menuPermissions || [];
  return permissions.includes(permissionKey);
};

/**
 * path da <Route> → chave. null = rota privada de propósito sem chave de matriz
 * (gate de perfil na própria página, ou super da plataforma).
 * String fora de ALL_MENU_KEYS = admin-only: o Get não devolve a chave, então
 * supervisor/user veem ForbiddenPage; admin passa por isFullAdmin.
 * null nesta fase: "/", "/tickets/:ticketId?" (sem gate de matriz) e
 * "/dashboard" (a página mantém o gate showDashboard).
 */
export const ROUTE_MENU_KEY = {
  "/financeiro": "management.financial",
  "/companies": null,
  "/admin-financeiro": null,
  "/stripe-payments": null,
  "/audit-logs": null,
  "/mobile-app-config": null,
  "/": null,
  "/dashboard": null,
  "/flows": "automation.flowbuilder",
  "/flows/:flowId": "automation.flowbuilder",
  "/tickets/:ticketId?": null,
  "/connections": "connections.connections",
  "/ifood/orders": "ifood.orders",
  "/ifood/catalog": "ifood.catalog",
  "/ifood/reviews": "ifood.reviews",
  "/ifood/financial": "ifood.financial",
  "/ifood/promotions": "ifood.promotions",
  "/quick-messages": "tickets.quickMessages",
  "/todolist": "management.todo",
  "/tasks": "management.tasks",
  "/leads": "management.funnel.leads",
  "/automations": "management.funnel.automations",
  "/journey": "management.funnel.journey",
  "/callbacks": "tickets.callbacks",
  "/schedules": "tickets.schedules",
  "/tags": "tickets.tags",
  "/contacts": "tickets.contacts",
  "/contacts/import": "tickets.contactsImport",
  "/helps": "tickets.helps",
  "/help-center/:categoryId?/:articleId?": "tickets.helpCenter",
  "/users": "administration.users",
  "/messages-api": "automation.messagesAPI",
  "/settings": "administration.settings",
  "/queues": "administration.queues",
  "/whatsapp-flows": "connections.whatsappFlows",
  "/whatsapp-flows/:id/edit": "connections.whatsappFlows",
  "/whatsapp-flows/:id/analytics": "connections.whatsappFlows",
  "/contact-wallets": "tickets.contactWallets",
  "/profile-permissions": "administration.profilePermissions",
  "/storage-management": "administration.storageManagement",
  "/backup-restore": "administration.backupRestore",
  "/mobile-app-qr": null,
  "/reports": "management.reports",
  "/reports-advanced": "management.advancedReports",
  "/ticket-classification": "management.ticketClassification",
  "/ticket-tabs-settings": "management.ticketTabs",
  "/queue-integration": "automation.queueIntegration",
  "/integrations/google-speech": "automation.googleSpeech",
  "/integrations/voice-api": "automation.voiceAI",
  "/integrations/public-api": "automation.publicApi",
  "/announcements": "management.announcements",
  "/chats/:id?": "tickets.internalChat",
  "/files": "administration.files",
  "/moments": "management.panel",
  "/stalled-tickets": "management.stalledTickets",
  "/Kanban": "management.funnel.kanban",
  "/TagsKanban": "management.tagsKanban",
  "/ai-configurations": "automation.talkAI",
  "/ai-tools": "automation.aiTools",
  "/ai-tools/web-sessions": "automation.aiTools",
  "/ai-playground": "automation.aiTools",
  "/smart-ai-dashboard": "management.smartAI",
  "/email-connections": "connections.email",
  "/email-templates": "connections.email",
  "/whatsapp-groups": "connections.whatsappGroups",
  "/whatsapp-communities": "connections.whatsappCommunities",
  "/whatsapp-channels": "connections.whatsappChannels",
  "/allConnections": null,
  "/contact-lists": "campaigns.contactLists",
  "/contact-lists/:contactListId/contacts": "campaigns.contactLists",
  "/campaigns": "campaigns.campaigns",
  "/campaign/:campaignId/report": "campaigns.campaigns",
  "/campaign-blacklist": "campaigns.blacklist",
  "/campaign-templates": "campaigns.templates"
};

/** Usado pelos testes. Caminho fora do mapa é erro. */
export const menuKeyForPathStrict = path => {
  if (!Object.prototype.hasOwnProperty.call(ROUTE_MENU_KEY, path)) {
    throw new Error(`rota sem mapa: ${path}`);
  }
  return ROUTE_MENU_KEY[path];
};

/**
 * Não lança: um path errado no drawer derrubaria o render de todo mundo.
 * Em dev avisa; em produção devolve null (sem gate).
 */
export const menuKeyForPath = path => {
  if (!Object.prototype.hasOwnProperty.call(ROUTE_MENU_KEY, path)) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`rota sem mapa: ${path}`);
    }
    return null;
  }
  return ROUTE_MENU_KEY[path];
};

export const buildParentChildrenMap = menuStructure => {
  const map = {};
  (menuStructure || []).forEach(category => {
    (category.items || []).forEach(item => {
      if (Array.isArray(item.children) && item.children.length > 0) {
        map[item.key] = item.children.slice();
      }
    });
  });
  return map;
};
